import { Navigate, Outlet } from 'react-router-dom'

import { MotionConfig } from 'motion/react'

import { useAccount, useIsOneBufferOrg } from '~publish/contexts/AccountContext'
import { useCommunityCountsContext } from '~publish/contexts/CommunityCountsContext'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { BackfillProvider } from '~publish/pages/Community/BackfillContext'
import { ReplyDraftsProvider } from '~publish/pages/Community/Comments/context/ReplyDraftsContext'
import { useCommentsBackfill } from '~publish/pages/Community/hooks/backfill/useCommentsBackfill'
import { useCommunityLoadedTracking } from '~publish/pages/Community/hooks/tracking/useCommunityLoadedTracking'

import type { JSX } from 'react'

const CommentsContent = (): JSX.Element => {
  // Auto-trigger backfill for polling channels and handle Pusher events globally
  // This component stays mounted for all /comments routes, ensuring Pusher listeners remain active
  useCommentsBackfill()

  const {
    totalOrgCount,
    totalOrgMentionCount,
    mentionsEnabled,
    mentionsReady,
    loading,
    error,
  } = useCommunityCountsContext()
  useCommunityLoadedTracking({
    unansweredCommentsCount: totalOrgCount,
    unansweredMentionsCount: mentionsEnabled ? totalOrgMentionCount : undefined,
    // Wait for the mentions split to resolve so the one-shot event doesn't latch
    // before we know whether mentions count (and their total is settled).
    loading: loading || error !== null || !mentionsReady,
  })

  return <Outlet />
}

export const CommentsRouteHandler = (): JSX.Element | null => {
  const { loading } = useAccount()
  const isOneBufferOrganization = useIsOneBufferOrg()

  // Set page title for all comments routes
  usePageTitle('Community')

  if (loading) {
    return null
  }

  if (!isOneBufferOrganization) {
    return <Navigate to="/" replace />
  }

  // Render BackfillProvider + CommentsContent to keep Pusher listeners mounted
  return (
    <MotionConfig reducedMotion="user">
      <BackfillProvider>
        <ReplyDraftsProvider>
          <CommentsContent />
        </ReplyDraftsProvider>
      </BackfillProvider>
    </MotionConfig>
  )
}
